import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useSetRecoilState } from 'recoil';
import { enqueueSnackbar } from 'notistack';
import {
    useGetDicomPatients,
    useGetDicomPatientStudies,
    useGetPatients,
    useListTargets,
    fetchDownloadDicomImage,
    fetchCreatePatient,
    fetchSyncPatient,
} from '~/backend/connector-api/connector-components';
import {
    DICOMPatientPresentation,
    PatientPresentation,
    SliceDTO,
    SeriesSyncPayload,
} from '~/backend/connector-api/connector-schemas';
import useProcessDicomFiles from '~/hooks/useProcessDicomFiles';
import usePatientGuard from '~/hooks/patient/usePatientGuard';
import useUnloadPatient from '~/hooks/patient/useUnloadPatient';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import { MultiPatientConflictAction } from '~/components/Dialogs/MultiPatientConflictDialog';
import { selectedPatientIdState } from '~/state/atoms/patientAtoms';
import AppConfig from '~/utils/appconfig';
import { formatDate as parseDicomDate, formatSeriesDate } from '~/utils/date';
import { SelectedImage } from '~/containers/patients-modal/DicomSourcesModal/types';

const DOWNLOAD_CONCURRENCY = 6;
const SYNC_INITIAL_WAIT_MS = 3000; // Wait for sync to start
const DOWNLOAD_RETRY_DELAY_MS = 1000;
const DOWNLOAD_MAX_RETRIES = 3;
const SEARCH_DEBOUNCE_MS = 500;

export type DicomImportView = 'search' | 'series';

export type DicomImportPhase =
    | 'idle'
    | 'syncing'
    | 'downloading'
    | 'processing';

export interface DicomImportProgress {
    phase: DicomImportPhase;
    current: number;
    total: number;
}

interface UseDicomImportOptions {
    onImportComplete?: () => void;
}

export default function useDicomImport(options?: UseDicomImportOptions) {
    const intl = useIntl();
    const [view, setView] = useState<DicomImportView>('search');
    const [query, setQuery] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    // Debounced auto-search: update searchTerm after user stops typing
    useEffect(() => {
        const trimmed = query.trim();
        if (!trimmed) {
            setSearchTerm('');
            return;
        }
        const timer = setTimeout(() => {
            setSearchTerm(trimmed);
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [query]);

    const [selectedPatient, setSelectedPatient] =
        useState<DICOMPatientPresentation | null>(null);
    const [selectedImages, setSelectedImages] = useState<SelectedImage[]>([]);
    const [selectedTargetName, setSelectedTargetName] = useState<string | null>(
        null
    );
    const [loadingSeriesDownload, setLoadingSeriesDownload] = useState(false);
    const [importProgress, setImportProgress] = useState<DicomImportProgress>({
        phase: 'idle',
        current: 0,
        total: 0,
    });

    // Stable ref for callback to avoid dependency churn
    const onImportCompleteRef = useRef(options?.onImportComplete);
    useEffect(() => {
        onImportCompleteRef.current = options?.onImportComplete;
    }, [options?.onImportComplete]);

    const setSelectedPatientId = useSetRecoilState(selectedPatientIdState);
    const { processImportedFiles, patientConflict, resolvePatientConflict } =
        useProcessDicomFiles();
    const { initializeEnvironment } = useViewportEnvironment();
    const { checkPatientConflict } = usePatientGuard();
    const { unloadCurrentPatient } = useUnloadPatient();

    // State for the early (pre-download) conflict dialog shown when
    // the user selects a patient that differs from the loaded one.
    const [earlyConflict, setEarlyConflict] = useState<{
        open: boolean;
        loadedPatientId: string;
        incomingPatientIds: string[];
        hasUnsavedChanges: boolean;
        clinicalUse: boolean;
    }>({
        open: false,
        loadedPatientId: '',
        incomingPatientIds: [],
        hasUnsavedChanges: false,
        clinicalUse: true,
    });
    const earlyConflictResolverRef = useRef<
        ((action: MultiPatientConflictAction) => void) | null
    >(null);

    const resolveEarlyConflict = useCallback(
        (action: MultiPatientConflictAction) => {
            setEarlyConflict((prev) => ({ ...prev, open: false }));
            earlyConflictResolverRef.current?.(action);
            earlyConflictResolverRef.current = null;
        },
        []
    );

    const { data: targetsData } = useListTargets(
        {},
        { staleTime: 60 * 1000, enabled: AppConfig.isConnectorMode }
    );

    const availableTargets = useMemo(() => {
        if (
            !targetsData ||
            typeof targetsData !== 'object' ||
            Array.isArray(targetsData)
        ) {
            return [];
        }
        return Object.entries(targetsData)
            .filter(
                ([, direction]) => direction === 'In' || direction === 'InOut'
            )
            .map(([name]) => name);
    }, [targetsData]);

    // Auto-select first available target on initial load (null = uninitialized)
    useEffect(() => {
        if (availableTargets.length > 0 && selectedTargetName === null) {
            setSelectedTargetName(availableTargets[0]);
        }
    }, [availableTargets, selectedTargetName]);

    const { data: serverPatientsData, isLoading: isLoadingServerPatients } =
        useGetPatients(
            { queryParams: { series: true } },
            { staleTime: 10_000, enabled: AppConfig.isConnectorMode }
        );

    const serverPatients = useMemo<DICOMPatientPresentation[]>(() => {
        if (!serverPatientsData || !Array.isArray(serverPatientsData)) {
            return [];
        }
        return serverPatientsData.map(
            (p: PatientPresentation): DICOMPatientPresentation => ({
                patientID: p.patientID,
                patientName: p.patientName,
                studies: p.studies,
            })
        );
    }, [serverPatientsData]);

    const {
        data: patients,
        error,
        isLoading,
        isFetching,
    } = useGetDicomPatients(
        {
            queryParams: {
                patientIDTerm: searchTerm,
                targetName: selectedTargetName ?? undefined,
            },
        },
        {
            staleTime: 10 * 1000,
            enabled:
                searchTerm.length > 0 &&
                selectedTargetName !== null &&
                selectedTargetName.length > 0,
        }
    );

    const { data: detailedPatientData, isLoading: isLoadingStudies } =
        useGetDicomPatientStudies(
            {
                pathParams: { patientID: selectedPatient?.patientID || '' },
                queryParams: { targetName: selectedTargetName ?? '' },
            },
            {
                enabled:
                    !!selectedPatient?.patientID &&
                    !!selectedTargetName &&
                    selectedTargetName.length > 0,
                staleTime: 10 * 1000,
            }
        );

    const detailedPatient = useMemo(() => {
        if (!detailedPatientData) {
            return undefined;
        }
        // API returns DICOMPatientPresentation[] — extract first element
        if (Array.isArray(detailedPatientData)) {
            return detailedPatientData[0] as
                | DICOMPatientPresentation
                | undefined;
        }
        return detailedPatientData as DICOMPatientPresentation;
    }, [detailedPatientData]);

    const trimmedQuery = query.trim();
    const isSearchPending =
        trimmedQuery.length > 0 && trimmedQuery !== searchTerm;

    const filteredPatients = useMemo(() => {
        if (
            trimmedQuery !== searchTerm ||
            !patients ||
            !Array.isArray(patients)
        ) {
            return [];
        }

        return [...patients].sort((a, b) => {
            return (a.patientName || '').localeCompare(b.patientName || '');
        });
    }, [patients, trimmedQuery, searchTerm]);

    // Set of selected series keys for O(1) lookups
    const selectedSeriesKeys = useMemo(() => {
        const keys = new Set<string>();
        for (const img of selectedImages) {
            keys.add(`${img.patientId}::${img.seriesInstanceUID}`);
        }
        return keys;
    }, [selectedImages]);

    const resetState = useCallback(() => {
        setView('search');
        setSelectedPatient(null);
        setSelectedImages([]);
        setQuery('');
        setSearchTerm('');
        setSelectedTargetName(null);
    }, []);

    const handleSearch = useCallback(() => {
        setSearchTerm(trimmedQuery);
    }, [trimmedQuery]);

    const handleSelectPatient = useCallback(
        (patient: DICOMPatientPresentation) => {
            setSelectedPatient(patient);
            setSelectedImages([]);
            const autoTarget =
                patient.target_names?.[0] ||
                selectedTargetName ||
                availableTargets[0] ||
                '';
            setSelectedTargetName(autoTarget);
            setView('series');
        },
        [selectedTargetName, availableTargets]
    );

    const handleBackToSearch = useCallback(() => {
        setSelectedPatient(null);
        setSelectedImages([]);
        setSelectedTargetName(null);
        setView('search');
    }, []);

    const toggleSeriesSelection = useCallback(
        (
            patientId: string,
            studyInstanceUID: string,
            seriesInstanceUID: string
        ) => {
            setSelectedImages((prev) => {
                const exists = prev.some(
                    (img) =>
                        img.patientId === patientId &&
                        img.seriesInstanceUID === seriesInstanceUID
                );
                return exists
                    ? prev.filter(
                          (img) =>
                              !(
                                  img.patientId === patientId &&
                                  img.seriesInstanceUID === seriesInstanceUID
                              )
                      )
                    : [
                          ...prev,
                          { patientId, studyInstanceUID, seriesInstanceUID },
                      ];
            });
        },
        []
    );

    const isSeriesSelected = useCallback(
        (patientId: string, seriesInstanceUID: string) => {
            return selectedSeriesKeys.has(`${patientId}::${seriesInstanceUID}`);
        },
        [selectedSeriesKeys]
    );

    const formatDate = useCallback((dateString: string) => {
        // Try DICOM DA format (YYYYMMDD) first
        const parsed = parseDicomDate(dateString);
        if (parsed && !isNaN(parsed.getTime())) {
            return formatSeriesDate(parsed) || dateString;
        }
        // Fallback: try as ISO date string
        const isoDate = new Date(dateString);
        if (!isNaN(isoDate.getTime())) {
            return formatSeriesDate(isoDate) || dateString;
        }
        return dateString;
    }, []);

    const handleLoadSelectedSeries = useCallback(async () => {
        if (
            selectedImages.length === 0 ||
            !detailedPatient ||
            !selectedTargetName
        ) {
            return;
        }

        const patientId = detailedPatient.patientID || '';
        const patientName = detailedPatient.patientName || patientId;

        // Early patient conflict check — before downloading anything
        let earlyDismissed = false;
        const guardResult = checkPatientConflict([patientId]);
        if (guardResult.action === 'conflict') {
            const userAction = await new Promise<MultiPatientConflictAction>(
                (resolve) => {
                    earlyConflictResolverRef.current = resolve;
                    setEarlyConflict({
                        open: true,
                        loadedPatientId: guardResult.loadedPatientId,
                        incomingPatientIds: guardResult.incomingPatientIds,
                        hasUnsavedChanges: guardResult.hasUnsavedChanges,
                        clinicalUse: guardResult.clinicalUse,
                    });
                }
            );

            if (userAction === 'discard') {
                return;
            }

            if (userAction === 'switch') {
                // Unload existing patient before proceeding
                unloadCurrentPatient();
            }

            if (userAction === 'dismiss') {
                // Non-clinical: user chose to load both patients.
                // Pass this decision through so the file-processing
                // hook skips its own conflict check.
                earlyDismissed = true;
            }
        }

        const resetProgress = () => {
            setImportProgress({ phase: 'idle', current: 0, total: 0 });
        };

        try {
            setLoadingSeriesDownload(true);

            // Step 1: Create the patient in connector
            try {
                await fetchCreatePatient({
                    body: { patientID: patientId, patientName },
                });
            } catch {
                // Patient may already exist, continue
            }

            // Step 2: Build sync payloads for selected series
            const syncPayloads: SeriesSyncPayload[] = [];
            for (const image of selectedImages) {
                const study = detailedPatient.studies?.find(
                    (s) => s.studyInstanceUID === image.studyInstanceUID
                );
                const series = study?.series?.find(
                    (s) => s.seriesInstanceUID === image.seriesInstanceUID
                );
                if (!series?.slices?.length) {
                    continue;
                }

                syncPayloads.push({
                    dicomSystemName: selectedTargetName,
                    modality: series.modality || 'CT',
                    studyInstanceUID: image.studyInstanceUID,
                    seriesInstanceUID: image.seriesInstanceUID,
                    datasets: series.slices.map((slice) => ({
                        modality: slice.modality || series.modality || 'CT',
                        studyInstanceUID: image.studyInstanceUID,
                        seriesInstanceUID: image.seriesInstanceUID,
                        sopInstanceUID: slice.sopInstanceUID,
                    })),
                });
            }

            if (syncPayloads.length === 0) {
                enqueueSnackbar(
                    intl.formatMessage({ id: 'noSeriesAvailable' }),
                    { variant: 'warning' }
                );
                return;
            }

            // Step 3: Sync slices with connector (tell it to download from DICOM source)
            const totalSlices = syncPayloads.reduce(
                (acc, p) => acc + p.datasets.length,
                0
            );
            setImportProgress({
                phase: 'syncing',
                current: 0,
                total: totalSlices,
            });

            await fetchSyncPatient({
                pathParams: { patientID: patientId },
                body: syncPayloads,
            });

            // Brief wait for sync to start, then download with retries
            await new Promise((resolve) =>
                setTimeout(resolve, SYNC_INITIAL_WAIT_MS)
            );

            // Step 4: Download slices to frontend
            setImportProgress({
                phase: 'downloading',
                current: 0,
                total: totalSlices,
            });

            const downloadTasks: {
                studyInstanceUID: string;
                seriesInstanceUID: string;
                slice: SliceDTO;
            }[] = [];
            for (const image of selectedImages) {
                const study = detailedPatient.studies?.find(
                    (s) => s.studyInstanceUID === image.studyInstanceUID
                );
                const series = study?.series?.find(
                    (s) => s.seriesInstanceUID === image.seriesInstanceUID
                );
                if (!series?.slices?.length) {
                    continue;
                }
                for (const slice of series.slices) {
                    downloadTasks.push({
                        studyInstanceUID: image.studyInstanceUID,
                        seriesInstanceUID: image.seriesInstanceUID,
                        slice,
                    });
                }
            }

            const allFiles: File[] = [];
            let filesDownloaded = 0;
            const totalFiles = downloadTasks.length;

            // Download a single slice with retries
            const downloadSlice = async (
                studyId: string,
                seriesId: string,
                slice: SliceDTO
            ): Promise<File | null> => {
                for (let retry = 0; retry <= DOWNLOAD_MAX_RETRIES; retry++) {
                    try {
                        const blob = await fetchDownloadDicomImage({
                            pathParams: {
                                studyId,
                                seriesId,
                                instanceId: slice.sopInstanceUID,
                            },
                        });
                        if (blob) {
                            return new File(
                                [blob],
                                `${slice.sopInstanceUID}.dcm`
                            );
                        }
                    } catch {
                        if (retry < DOWNLOAD_MAX_RETRIES) {
                            // Wait before retry (connector might still be fetching from DICOM source)
                            await new Promise((resolve) =>
                                setTimeout(
                                    resolve,
                                    DOWNLOAD_RETRY_DELAY_MS * (retry + 1)
                                )
                            );
                        }
                    }
                }
                return null;
            };

            // Update progress after each download
            const updateProgress = () => {
                filesDownloaded++;
                setImportProgress({
                    phase: 'downloading',
                    current: filesDownloaded,
                    total: totalFiles,
                });
            };

            for (
                let i = 0;
                i < downloadTasks.length;
                i += DOWNLOAD_CONCURRENCY
            ) {
                const batch = downloadTasks.slice(i, i + DOWNLOAD_CONCURRENCY);
                const results = await Promise.all(
                    batch.map(
                        async ({
                            studyInstanceUID,
                            seriesInstanceUID,
                            slice,
                        }) => {
                            const file = await downloadSlice(
                                studyInstanceUID,
                                seriesInstanceUID,
                                slice
                            );
                            updateProgress();
                            return file;
                        }
                    )
                );
                for (const file of results) {
                    if (file) {
                        allFiles.push(file);
                    }
                }
            }

            if (allFiles.length > 0) {
                setImportProgress({
                    phase: 'processing',
                    current: 0,
                    total: allFiles.length,
                });
                const importedItems = await processImportedFiles(
                    allFiles,
                    earlyDismissed ? { allowMultiplePatients: true } : undefined
                );
                if (importedItems) {
                    const patientId = importedItems.patients[0]?.PatientID;
                    if (patientId) {
                        setSelectedPatientId(patientId);
                    }
                    // Match the local drag-and-drop flow: initializing
                    // the viewport environment triggers the single-
                    // candidate auto-selection in setEnvironmentPrimaryImage,
                    // so a lone imported series becomes the primary image
                    // without requiring manual selection.
                    await initializeEnvironment(importedItems);
                    enqueueSnackbar(
                        intl.formatMessage({ id: 'importSuccessful' }),
                        { variant: 'success' }
                    );
                    resetState();
                    onImportCompleteRef.current?.();
                } else {
                    enqueueSnackbar(
                        intl.formatMessage({ id: 'noSeriesAvailable' }),
                        { variant: 'warning' }
                    );
                }
            }
        } catch (err) {
            enqueueSnackbar(
                intl.formatMessage({ id: 'errorLoadingPatients' }),
                { variant: 'error' }
            );
        } finally {
            setLoadingSeriesDownload(false);
            resetProgress();
        }
    }, [
        selectedImages,
        detailedPatient,
        selectedTargetName,
        processImportedFiles,
        initializeEnvironment,
        setSelectedPatientId,
        resetState,
        intl,
        checkPatientConflict,
        unloadCurrentPatient,
    ]);

    const exposedTargetName = selectedTargetName ?? '';

    return useMemo(
        () => ({
            view,
            query,
            setQuery,
            searchTerm,
            selectedTargetName: exposedTargetName,
            setSelectedTargetName: setSelectedTargetName as (
                name: string
            ) => void,
            availableTargets,
            filteredPatients,
            isSearchPending,
            selectedPatient,
            detailedPatient,
            selectedImages,
            loadingSeriesDownload,
            importProgress,
            isLoading,
            isFetching,
            isLoadingStudies,
            error,
            serverPatients,
            isLoadingServerPatients,
            handleSearch,
            handleSelectPatient,
            handleBackToSearch,
            toggleSeriesSelection,
            isSeriesSelected,
            formatDate,
            handleLoadSelectedSeries,
            resetState,
            // Patient conflict dialog state (early check + file-processing check)
            earlyConflict,
            resolveEarlyConflict,
            patientConflict,
            resolvePatientConflict,
        }),
        [
            view,
            query,
            searchTerm,
            exposedTargetName,
            availableTargets,
            filteredPatients,
            isSearchPending,
            selectedPatient,
            detailedPatient,
            selectedImages,
            loadingSeriesDownload,
            importProgress,
            isLoading,
            isFetching,
            isLoadingStudies,
            error,
            serverPatients,
            isLoadingServerPatients,
            handleSearch,
            handleSelectPatient,
            handleBackToSearch,
            toggleSeriesSelection,
            isSeriesSelected,
            formatDate,
            handleLoadSelectedSeries,
            resetState,
            earlyConflict,
            resolveEarlyConflict,
            patientConflict,
            resolvePatientConflict,
        ]
    );
}
